<script lang="ts">
  import { onMount } from 'svelte';
  import Header from './lib/components/Header.svelte';
  import HumanVerify from './lib/components/HumanVerify.svelte';
  import Selection from './lib/pages/Selection.svelte';
  import ChecklistView from './lib/pages/ChecklistView.svelte';
  import Summary from './lib/pages/Summary.svelte';
  import { session, currentStep, records, selectedChecklistId, fontSize } from './lib/stores';

  let humanVerified = $state(false);

  onMount(() => {
    // 檢查 sessionStorage 是否已經驗證過
    if (sessionStorage.getItem('dmy_verified') === '1') {
      humanVerified = true;
    }

    // 從 localStorage 恢復狀態
    const savedSession = localStorage.getItem('dmy_session');
    const savedRecords = localStorage.getItem('dmy_records');
    const savedStep = localStorage.getItem('dmy_step');
    const savedChecklistId = localStorage.getItem('dmy_checklist_id');

    if (savedSession) session.set(JSON.parse(savedSession));
    if (savedRecords) records.set(JSON.parse(savedRecords));
    if (savedStep && savedSession) {
      currentStep.set(savedStep as any);
    } else {
      currentStep.set('selection');
    }
    if (savedChecklistId) selectedChecklistId.set(savedChecklistId);

    // 恢復字型大小設定（預設由 store 提供為 'large'）
    const savedFont = localStorage.getItem('dmy_font_size') as 'small' | 'medium' | 'large' | null;
    if (savedFont) fontSize.set(savedFont);

    session.subscribe(val => {
      if (val) localStorage.setItem('dmy_session', JSON.stringify(val));
      else localStorage.removeItem('dmy_session');
    });

    records.subscribe(val => {
      localStorage.setItem('dmy_records', JSON.stringify(val));
    });

    currentStep.subscribe(val => {
      localStorage.setItem('dmy_step', val);
    });

    selectedChecklistId.subscribe(val => {
      if (val) localStorage.setItem('dmy_checklist_id', val);
      else localStorage.removeItem('dmy_checklist_id');
    });

    // 監聽字型設定並套用到 root CSS 變數
    fontSize.subscribe(size => {
      const map: Record<string, string> = { small: '16px', medium: '18px', large: '20px' };
      const px = map[size] ?? '20px';
      try {
        document.documentElement.style.setProperty('--base-font-size', px);
        localStorage.setItem('dmy_font_size', size);
      } catch (e) {
        // ignore (e.g., SSR)
      }
    });
  });

  function onHumanVerified() {
    humanVerified = true;
    sessionStorage.setItem('dmy_verified', '1');
  }
</script>

<svelte:head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover" />
  <meta name="mobile-web-app-capable" content="yes" />
  <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
</svelte:head>

<div class="min-h-screen h-screen flex flex-col bg-linear-to-b from-slate-50 to-slate-100 overflow-hidden">
  <Header />

  <main class="grow overflow-y-auto overscroll-contain px-3 sm:px-4 py-4 sm:py-6 container mx-auto w-full max-w-2xl">
    <div class="bg-white rounded-2xl sm:rounded-3xl shadow-lg p-4 sm:p-8 mb-8">
      {#if !humanVerified}
        <HumanVerify on:verified={onHumanVerified} />
      {:else if $currentStep === 'selection'}
        <Selection />
      {:else if $currentStep === 'checklist'}
        <ChecklistView />
      {:else if $currentStep === 'summary'}
        <Summary />
      {/if}
    </div>
  </main>

  <!-- Footer intentionally removed to save first-screen space for screenshots -->
</div>

<style>
  :global(html, body) {
    margin: 0;
    padding: 0;
    overflow: hidden;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans Traditional Chinese', 'Microsoft JhengHei', sans-serif;
    -webkit-font-smoothing: antialiased;
    -webkit-tap-highlight-color: transparent;
  }

  :global(input, button, select, textarea) {
    font-size: 16px; /* 防止 iOS 自動放大 */
  }
</style>
